import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { finalize, forkJoin, Subscription } from 'rxjs';
import { SidebarService } from '../../../core/service/sidebar.service';
import { MessageService } from '../../../core/service/message.service';
import { CompanyBankAccountService } from '../../../invoice/service/data/company-bank-account.service';
import { Bank } from '../../model/bank';
import { Company } from '../../model/company';
import { CompanyBankAccount } from '../../model/company-bank-account';
import { BankService } from '../../service/data/bank.service';

@Component({
  selector: 'app-company-bank-accounts',
  templateUrl: './company-bank-accounts.component.html',
  styleUrls: ['./company-bank-accounts.component.scss'],
  providers: [BankService, CompanyBankAccountService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CompanyBankAccountsComponent implements OnInit, OnDestroy {
  @ViewChild('bankAccountSidebar', { static: true }) bankAccountSidebar: TemplateRef<unknown>;

  @Input() company: Company;
  @Input() accountPlanId: string;
  @Output() bankAccountsChanged = new EventEmitter<CompanyBankAccount[]>();

  bankAccounts: CompanyBankAccount[] = [];
  banks: Bank[] = [];
  selectedAccount: CompanyBankAccount;
  loading = false;
  loadFailed = false;

  private readonly subscription = new Subscription();

  constructor(
    private readonly bankService: BankService,
    private readonly bankAccountService: CompanyBankAccountService,
    private readonly sidebarService: SidebarService,
    private readonly messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadAccounts();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.sidebarService.toggleSidebar(false);
  }

  loadAccounts(): void {
    if (!this.company?.id || this.loading) return;

    this.loading = true;
    this.loadFailed = false;
    this.subscription.add(
      forkJoin({
        accounts: this.bankAccountService.getByCompany(this.company.id),
        banks: this.bankService.get(),
      })
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: ({ accounts, banks }) => {
            this.bankAccounts = accounts;
            this.banks = banks;
            this.bankAccountsChanged.emit(accounts);
          },
          error: error => {
            this.loadFailed = true;
            this.messageService.showError(error);
          },
        })
    );
  }

  openCreate(): void {
    this.openEditor(Object.assign(new CompanyBankAccount(), { companyId: this.company.id }));
  }

  openEdit(account: CompanyBankAccount): void {
    this.openEditor(Object.assign(new CompanyBankAccount(), account));
  }

  closeEditor(): void {
    this.sidebarService.toggleSidebar(false);
    this.selectedAccount = null;
  }

  onAccountSaved(): void {
    this.closeEditor();
    this.loadAccounts();
  }

  onAccountDeleted(): void {
    this.closeEditor();
    this.loadAccounts();
  }

  bankName(account: CompanyBankAccount): string {
    const clearingNumber = Number(account?.clearingNumber);
    if (!Number.isFinite(clearingNumber)) return '';

    return this.banks.find(bank => clearingNumber >= bank.clearingNumberFrom && clearingNumber <= bank.clearingNumberTo)?.name ?? '';
  }

  accountNumber(account: CompanyBankAccount): string {
    return [account?.clearingNumber, account?.accountNumber].filter(Boolean).join('-');
  }

  private openEditor(account: CompanyBankAccount): void {
    this.selectedAccount = account;
    this.sidebarService.setSidebarContent({ template: this.bankAccountSidebar, onClose: () => (this.selectedAccount = null) });
    this.sidebarService.toggleSidebar(true);
  }
}
